import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import tagColor from 'webapp/helpers/tag-color';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { TagRecord } from 'webapp/models';
import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import type CustomQuery from 'webapp/models-custom/custom-query';
import type Forecast from 'webapp/models-custom/forecast';
import type Goal from 'webapp/models-custom/goal';
import type HorizonMetric from 'webapp/models-custom/horizon-metric';
import type Report from 'webapp/models-custom/report';
import type Script from 'webapp/models-custom/script';
import type Space from 'webapp/models-custom/space';

export type Model =
	| Board
	| Goal
	| Space
	| Script
	| CustomQuery
	| HorizonMetric
	| BoardLoop
	| Report
	| Forecast
	| { tags: number[]; tagsObjs?: TagRecord[] };

interface Args {
	model: Model;
	canEdit?: boolean;
	showSingleTagName?: boolean;
	maxShownTags?: number;
}

export default class TagContainerComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;

	@tracked isContextOpen = false;

	get allTags() {
		return this.args.model?.tagsObjs || [];
	}

	@cached
	get tags() {
		let tags = this.allTags;
		if (this.maxShownTags) {
			tags = tags.slice(0, this.maxShownTags);
		}
		return tags;
	}

	get showTags() {
		return this.tags.length > 0;
	}

	get isSingleTag() {
		return this.tags.length === 1;
	}

	get isMultipleTags() {
		return this.showTags && !this.isSingleTag;
	}

	get canEdit() {
		return this.args.canEdit ?? true;
	}

	get showSingleTagName() {
		return this.args.showSingleTagName ?? true;
	}

	get maxShownTags() {
		return this.args.maxShownTags ?? 10;
	}

	get firstTag() {
		return this.tags[0];
	}

	get tooltipText() {
		let text = '';

		const tags = this.allTags,
			countTags = tags.length,
			numShown = 5, // num of shown tag names in tooltip
			diff = countTags - numShown;

		tags.forEach((tag, i) => {
			if (i < numShown) {
				text += tag.name;

				if (diff > 0) {
					text += i < numShown - 1 ? ', ' : '';
				} else {
					text += i < countTags - 1 ? ', ' : '';
				}
			}
		});

		text += diff > 0 ? ' and ' + diff + ' other' : '';
		text += diff > 1 ? 's' : '';

		return text;
	}

	// showing tooltip always, except when we show name for single tag
	get showTooltip() {
		return !(this.showSingleTagName && this.tags.length === 1);
	}

	@action
	handleClick(e: PointerEvent) {
		if (!this.canEdit) {
			return;
		}

		if (this.accountService.handleDemo()) {
			return;
		}

		const attachTo = e.target as HTMLElement;

		if (!attachTo) {
			return;
		}

		const popup = this.mmTagsService.openTagsContext(attachTo, {
			model: this.args.model,
		});

		if (popup) {
			this.isContextOpen = true;

			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isContextOpen = false;
				}
			});
		}
	}
	<template>
		<div
			role="button"
			class={{joinClasses
				(array
					"tagCt"
					(if this.showTags "shown" "empty")
					(if this.isSingleTag "single")
					(if this.isMultipleTags "multiple")
					(if this.isContextOpen "contextOpen")
					(if this.canEdit "canEdit")
				)
			}}
			{{on "click" this.handleClick}}
		>
			{{#if this.showTags}}
				{{#if this.isSingleTag}}
					<div class="tag">
						<CommonUiIconComponent
							@name="TagFill"
							@size="xs"
							@fill={{tagColor this.firstTag.color}}
						/>
						{{#if this.showSingleTagName}}
							<span class="name">{{this.firstTag.name}}</span>
						{{/if}}
					</div>
				{{else}}
					{{#each this.tags as |tag|}}
						<div class="tag">
							<CommonUiIconComponent
								@name="TagFill"
								@size="xs"
								@fill={{tagColor tag.color}}
							/>
						</div>
					{{/each}}
				{{/if}}
				{{#if this.showTooltip}}
					<ToolTipComponent @text={{this.tooltipText}} @position="top" />
				{{/if}}
			{{else}}
				<div class="tag">
					<CommonUiIconComponent @name="TagFill" @size="xs" />
				</div>
			{{/if}}
		</div>
	</template>
}
